// =============================================
// TransferWizard — stepper kreatora "Przenies domeny": 1 Domeny → 2 Akcje →
// 3 Plan → 4 Wykonanie. Stan w useTransferWizard (CloudflarePage), run w
// transferRunStore. Przy pierwszym wejsciu pobiera zony (obowiazkowe).
// =============================================
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import type { CloudflareAccount } from "@/hooks/useCloudflareAccounts";
import type { AllZonesState } from "@/hooks/useAllZones";
import type { TransferDomainsState } from "@/hooks/useTransferDomains";
import type { TransferWizardState, WizardStep } from "@/hooks/useTransferWizard";
import { Step1DomainPicker } from "./Step1DomainPicker";
import { Step2ActionsConfig } from "./Step2ActionsConfig";
import { Step3PlanTable } from "./Step3PlanTable";
import { Step4Runner } from "./Step4Runner";

interface Props {
  accounts: CloudflareAccount[];
  loading: boolean;
  allZones: AllZonesState;
  transferDomains: TransferDomainsState;
  wizard: TransferWizardState;
  onShowHistory: () => void;
}

const STEPS: Array<{ n: WizardStep; label: string }> = [
  { n: 1, label: "Domeny" }, { n: 2, label: "Akcje" }, { n: 3, label: "Plan" }, { n: 4, label: "Wykonanie" },
];

export const TransferWizard = ({ accounts, loading, allZones, transferDomains, wizard, onShowHistory }: Props) => {
  const { step, setStep, canNext1, canNext2, run, selectedKeys } = wizard;

  // Zony obowiazkowe w kreatorze — pobieramy przy pierwszym wejsciu (ZonesTab zostaje leniwe).
  useEffect(() => {
    if (accounts.length && allZones.loadedAt === null && !allZones.loading) void allZones.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  if (!loading && accounts.length === 0) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">Najpierw dodaj konto w zakładce <strong>Konta</strong>.</CardContent></Card>;
  }

  const running = run.status === "running";
  const next = () => setStep((Math.min(4, step + 1)) as WizardStep);
  const back = () => setStep((Math.max(1, step - 1)) as WizardStep);
  const canNext = step === 1 ? canNext1 : step === 2 ? canNext2 : false;
  const nextHint =
    step === 1 && selectedKeys.size === 0 ? "Zaznacz domeny"
      : step === 1 && allZones.loading ? "Ładuję zony z kont CF…"
        : step === 1 && allZones.loadedAt === null ? "Pobierz zony"
          : step === 1 && !canNext1 ? "Zony nie pobrane z żadnego konta"
            : step === 2 && !canNext2 ? "Włącz co najmniej jedną akcję (A wymaga konta)" : "";

  return (
    <div className="space-y-5">
      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        {STEPS.map((s, i) => {
          const active = s.n === step;
          const done = s.n < step;
          const clickable = !running && (s.n < step || (s.n === 2 && canNext1) || (s.n === 3 && canNext1 && canNext2)) && s.n !== 4;
          return (
            <div key={s.n} className="flex items-center gap-2">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => clickable && setStep(s.n)}
                className={`flex items-center gap-2 rounded-full px-3 py-1 border text-xs ${active ? "bg-primary text-primary-foreground border-primary" : done ? "border-emerald-500/40 text-emerald-400" : "text-muted-foreground"} ${clickable ? "cursor-pointer" : "cursor-default"}`}
              >
                <span className="font-semibold">{s.n}</span>{s.label}
                {s.n === 4 && running && <Loader2 className="h-3 w-3 animate-spin" />}
              </button>
              {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </div>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          {step > 1 && step < 4 && <Button variant="outline" size="sm" onClick={back}><ChevronLeft className="h-4 w-4 mr-1" />Wstecz</Button>}
          {step < 3 && (
            <Button size="sm" onClick={next} disabled={!canNext} title={nextHint}>
              {step === 1 && allZones.loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Dalej<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {nextHint && step < 3 && <span className="text-xs text-muted-foreground hidden md:inline">{nextHint}</span>}
        </div>
      </div>

      {step === 1 && <Step1DomainPicker accounts={accounts} allZones={allZones} transferDomains={transferDomains} wizard={wizard} />}
      {step === 2 && <Step2ActionsConfig accounts={accounts} allZones={allZones} wizard={wizard} />}
      {step === 3 && <Step3PlanTable wizard={wizard} />}
      {step === 4 && <Step4Runner wizard={wizard} onShowHistory={onShowHistory} />}
    </div>
  );
};
